// MarketingV2 çoklu-dil bridge (client-side). Backend'in MarketingLangJson'ının JS karşılığı.
// interleaved DataJson ({leaf:{en,tr,...}}) ↔ dil-bazlı dilimler. Backend'e DataJson gider;
// backend her kayıtta Translates'i DataJson'dan yeniden türetir.

export const LANGS = ["en", "tr", "de", "fr", "es", "it", "pt", "ar"] as const;
export type Lang = (typeof LANGS)[number];
export const LANG_LABEL: Record<Lang, string> = {
  en: "English", tr: "Türkçe", de: "Deutsch", fr: "Français",
  es: "Español", it: "Italiano", pt: "Português", ar: "العربية",
};

const LSET = new Set<string>(LANGS as unknown as string[]);

export const isLangMap = (o: any): boolean =>
  !!o && typeof o === "object" && !Array.isArray(o) &&
  Object.keys(o).length > 0 && Object.keys(o).every((k) => LSET.has(k));

export const containsLang = (t: any): boolean =>
  isLangMap(t) ? true
  : Array.isArray(t) ? t.some(containsLang)
  : t && typeof t === "object" ? Object.values(t).some(containsLang)
  : false;

export const langLeafIsText = (o: any): boolean =>
  Object.values(o).every((v) => typeof v === "string");

// interleaved düğüm → tek-dil dilimi
export const deinterleave = (node: any, lang: Lang): any => {
  if (isLangMap(node)) return node[lang] ?? node.en ?? "";
  if (Array.isArray(node)) return node.map((x) => deinterleave(x, lang));
  if (node && typeof node === "object") {
    const r: any = {};
    for (const k in node) r[k] = deinterleave(node[k], lang);
    return r;
  }
  return node;
};

// iskelet (orijinal DataJson) + dil dilimleri → interleaved DataJson
export const reinterleave = (skel: any, perLang: Record<Lang, any>): any => {
  if (isLangMap(skel)) {
    const o: any = {};
    for (const l of LANGS) o[l] = perLang[l];
    return o;
  }
  if (Array.isArray(skel)) {
    return skel.map((_: any, i: number) =>
      reinterleave(
        skel[i],
        Object.fromEntries(LANGS.map((l) => [l, perLang[l]?.[i]])) as Record<Lang, any>
      )
    );
  }
  if (skel && typeof skel === "object") {
    const r: any = {};
    for (const k in skel)
      r[k] = reinterleave(
        skel[k],
        Object.fromEntries(LANGS.map((l) => [l, perLang[l]?.[k]])) as Record<Lang, any>
      );
    return r;
  }
  return skel;
};

// Bir DataJson'un top-level yerelleştirilebilir alanları + tipleri.
export type FieldKind = "text" | "textarea" | "json";
export interface LocField {
  key: string;
  kind: FieldKind;
}
export const localizableFields = (data: any): LocField[] => {
  const out: LocField[] = [];
  if (!data || typeof data !== "object") return out;
  for (const k of Object.keys(data)) {
    const v = data[k];
    if (!containsLang(v)) continue;
    if (isLangMap(v)) {
      if (langLeafIsText(v)) {
        const long = Object.values(v).some((s) => typeof s === "string" && s.length > 80);
        out.push({ key: k, kind: long ? "textarea" : "text" });
      } else {
        out.push({ key: k, kind: "json" }); // points/body → dil-başına yapılandırılmış
      }
    } else {
      out.push({ key: k, kind: "json" }); // caps/flows/pains/benefits/proof/testimonial/faqs
    }
  }
  return out;
};

// Boş dilleri İngilizce ile doldur (karar: boş→en). perLang dilimleri üzerinde.
export const fillEmptyWithEn = (
  skel: any,
  perLang: Record<Lang, any>
): Record<Lang, any> => {
  const walk = (s: any, vals: Record<Lang, any>): Record<Lang, any> => {
    if (isLangMap(s)) {
      const en = vals.en;
      const r: any = {};
      for (const l of LANGS) {
        const val = vals[l];
        const empty =
          val == null ||
          (typeof val === "string" && val.trim() === "") ||
          (Array.isArray(val) && val.length === 0);
        r[l] = empty ? en : val;
      }
      return r as Record<Lang, any>;
    }
    if (Array.isArray(s)) {
      return Object.fromEntries(
        LANGS.map((l) => [l, vals[l]])
      ) as Record<Lang, any>; // dizi eleman-eleman reinterleave'de ele alınır
    }
    return vals;
  };
  // Basit: reinterleave sırasında leaf bazında doldurmak yerine, perLang'i olduğu gibi bırakıp
  // reinterleave'den sonra leaf doldurma yapmak daha güvenli. Burada perLang'i aynen döneriz;
  // asıl doldurma buildDataJson içinde leaf düzeyinde yapılır.
  return perLang;
};

// perLang dilimlerinden DataJson üretir (iskelet = orijinal). Leaf düzeyinde boş→en doldurur.
export const buildDataJson = (skeleton: any, perLang: Record<Lang, any>): string => {
  const fill = (skel: any, vals: Record<Lang, any>): any => {
    if (isLangMap(skel)) {
      const en = vals.en;
      const o: any = {};
      for (const l of LANGS) {
        const v = vals[l];
        const empty =
          v == null ||
          (typeof v === "string" && v.trim() === "") ||
          (Array.isArray(v) && v.length === 0);
        o[l] = empty ? en : v;
      }
      return o;
    }
    if (Array.isArray(skel)) {
      return skel.map((_: any, i: number) =>
        fill(skel[i], Object.fromEntries(LANGS.map((l) => [l, vals[l]?.[i]])) as Record<Lang, any>)
      );
    }
    if (skel && typeof skel === "object") {
      const r: any = {};
      for (const k in skel)
        r[k] = fill(skel[k], Object.fromEntries(LANGS.map((l) => [l, vals[l]?.[k]])) as Record<Lang, any>);
      return r;
    }
    return skel;
  };
  return JSON.stringify(fill(skeleton, perLang));
};
